import useUserStore from "../states/user";

const DEFAULT_STORE_API_URL = "http://localhost:8085/service";

function normalizeBaseUrl(url) {
	return String(url || "").trim().replace(/\/$/, "");
}

function withServicePrefix(url) {
	const normalized = normalizeBaseUrl(url).replace(/\/api$/i, "");
	return /\/service$/i.test(normalized) ? normalized : `${normalized}/service`;
}

function getStoreApiBaseUrl() {
	const configured = normalizeBaseUrl(process.env.REACT_APP_STORE_API_URL || "");

	if (configured) {
		if (/^:\d+(\/.*)?$/.test(configured)) {
			return withServicePrefix(`http://localhost${configured}`);
		}

		if (/^localhost:\d+(\/.*)?$/i.test(configured)) {
			return withServicePrefix(`http://${configured}`);
		}

		return withServicePrefix(configured);
	}

	if (typeof window !== "undefined" && window.location?.hostname) {
		const protocol = window.location.protocol || "http:";
		const hostname = window.location.hostname;
		return `${protocol}//${hostname}:8085/service`;
	}

	return DEFAULT_STORE_API_URL;
}

const STORE_API_BASE_URL = getStoreApiBaseUrl();

function buildStoreApiUrl(path) {
	const normalizedPath = String(path || "").startsWith("/") ? path : `/${path}`;
	return `${STORE_API_BASE_URL}${normalizedPath}`;
}

function fetchWithOrganization(path, options = {}) {
	const organizationId = useUserStore.getState().session?.organization_id;
	if (!organizationId) {
		throw new Error("No organization found for this request. Please sign in again.");
	}

	const url = new URL(buildStoreApiUrl(path));
	const requestOptions = {
		credentials: "include",
		...options,
	};
	const method = (options.method || "GET").toUpperCase();
	if (method === "GET" || method === "HEAD") {
		url.searchParams.set("organization_id", organizationId);
	} else if (options.body instanceof FormData) {
		options.body.set("organization_id", String(organizationId));
	} else {
		requestOptions.headers = { ...options.headers, "Content-Type": "application/json" };
		requestOptions.body = JSON.stringify({
			...(options.body ? JSON.parse(options.body) : {}),
			organization_id: organizationId,
		});
	}
	return fetch(url.toString(), requestOptions);
}

export async function requestJson(path, options = {}) {
	const response = await fetchWithOrganization(path, options);

	let payload = {};
	try {
		payload = await response.json();
	} catch {
		payload = {};
	}

	return { response, payload };
}

export async function requestBlob(path, fallbackMessage) {
	const response = await fetchWithOrganization(path);
	if (!response.ok) {
		let payload = {};
		try {
			payload = await response.json();
		} catch {
			payload = {};
		}
		throw new Error(payload.error || fallbackMessage);
	}
	return response.blob();
}

export function assertOk(response, payload, fallbackMessage) {
	if (!response.ok) {
		const message = payload?.error || fallbackMessage;
		const detail = response.status ? `${message} (${response.status})` : message;
		throw new Error(detail);
	}

	return payload;
}

export async function fetchDiscounts() {
	const { response, payload } = await requestJson("/discount/index");
	assertOk(response, payload, "Failed to load discounts");
	if (!Array.isArray(payload?.discounts) || payload.discounts.some((discount) =>
		!Number.isSafeInteger(discount?.discount_id) || discount.discount_id <= 0 ||
		typeof discount.name !== "string" || typeof discount.code !== "string" ||
		!Number.isInteger(discount.percentage) || discount.percentage < 1 || discount.percentage > 100 ||
		!Number.isSafeInteger(discount.max_usage) || discount.max_usage < 1 ||
		!Number.isSafeInteger(discount.usage_count) || discount.usage_count < 0 ||
		typeof discount.expires_at !== "string" || !Number.isFinite(Date.parse(discount.expires_at))
	)) {
		throw new Error("The server returned an invalid discount list.");
	}
	return payload.discounts;
}

export async function createDiscount(discount) {
	const { response, payload } = await requestJson("/discount/create", {
		method: "POST",
		body: JSON.stringify(discount),
	});
	return assertOk(response, payload, "Failed to create discount");
}

export async function updateDiscount(discount) {
	const { response, payload } = await requestJson("/discount/update", {
		method: "PUT",
		body: JSON.stringify(discount),
	});
	return assertOk(response, payload, "Failed to update discount");
}

export async function deleteDiscount(discountId) {
	if (!Number.isSafeInteger(discountId) || discountId <= 0) {
		throw new Error("A valid discount ID is required.");
	}
	const { response, payload } = await requestJson(`/discount/delete/${discountId}`, { method: "DELETE" });
	return assertOk(response, payload, "Failed to delete discount");
}
